// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — CustomerOverridePage (Admin)
// Impersonate a customer to see their dashboard experience
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Crown, Coffee, Search, AlertTriangle, Lightbulb, Building2
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Button, Input, Grid, Badge } from '@components/ui/Primitives';
import { LoadingSpinner } from '@components/ui/States';

export function CustomerOverridePage() {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const { impersonate } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: customerData } = await supabase
        .from('profiles')
        .select('*, tenants(*)')
        .in('role', ['customer', 'owner', 'manager'])
        .eq('is_active', true);

      if (!customerData?.length) {
        setCustomers([]);
        setLoading(false);
        return;
      }

      // Enrich with alert counts
      const tenantIds = [...new Set(customerData.map(c => c.tenant_id).filter(Boolean))];

      let insightsCount = {};
      let gapsCount = {};

      if (tenantIds.length) {
        const [{ data: ins }, { data: gaps }] = await Promise.all([
          supabase.from('insights').select('tenant_id').in('tenant_id', tenantIds).eq('is_dismissed', false),
          supabase.from('financial_gaps').select('tenant_id').in('tenant_id', tenantIds).eq('is_resolved', false)
        ]);

        (ins || []).forEach(i => {
          insightsCount[i.tenant_id] = (insightsCount[i.tenant_id] || 0) + 1;
        });
        (gaps || []).forEach(g => {
          gapsCount[g.tenant_id] = (gapsCount[g.tenant_id] || 0) + 1;
        });
      }

      const enriched = customerData.map(c => ({
        ...c,
        insightsCount: insightsCount[c.tenant_id] || 0,
        gapsCount: gapsCount[c.tenant_id] || 0
      }));

      // Dedupe tenants: only one "owner" per tenant
      const seenTenants = new Set();
      const deduped = enriched.filter(c => {
        if (!c.tenant_id) return true;
        if (seenTenants.has(c.tenant_id)) return false;
        seenTenants.add(c.tenant_id);
        return true;
      });

      setCustomers(deduped);
      setLoading(false);
    })();
  }, []);

  const filtered = customers.filter(c => {
    const q = searchQuery.toLowerCase();
    return (
      (c.email || '').toLowerCase().includes(q) ||
      (c.full_name_ar || '').toLowerCase().includes(q) ||
      (c.full_name_en || '').toLowerCase().includes(q) ||
      (c.tenants?.name_ar || '').toLowerCase().includes(q) ||
      (c.tenants?.name_en || '').toLowerCase().includes(q)
    );
  });

  const handleImpersonate = useCallback((customer) => {
    if (window.confirm(
      lang === 'ar'
        ? `هل تريد الدخول على حساب ${customer.full_name_ar}؟`
        : `Enter dashboard as ${customer.full_name_en}?`
    )) {
      impersonate(customer);
    }
  }, [impersonate, lang]);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        icon={<Crown size={22} color={theme.primary} />}
        title={t.customerOverride}
        description={lang === 'ar'
          ? 'الدخول على حسابات العملاء لرؤية ما يرونه'
          : 'Enter customer accounts to see their view'
        }
      />

      <Card style={{ padding: '14px 20px', marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{
            position: 'absolute',
            [isRTL ? 'right' : 'left']: 12,
            top: 12,
            color: theme.textTertiary
          }} />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={lang === 'ar' ? 'بحث عن عميل...' : 'Search customers...'}
            style={{ [isRTL ? 'paddingRight' : 'paddingLeft']: 34 }}
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 60,
          color: theme.textTertiary,
          fontSize: 14
        }}>
          {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
        </div>
      ) : (
        <Grid min={300}>
          {filtered.map(c => (
            <Card key={c.id} style={{ padding: 22 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 16
              }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: theme.primaryDim,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Coffee size={22} color={theme.primary} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: theme.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {c.tenants
                      ? (lang === 'ar' ? c.tenants.name_ar : c.tenants.name_en)
                      : (lang === 'ar' ? c.full_name_ar : c.full_name_en)
                    }
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: theme.textSecondary,
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {c.email}
                  </div>
                </div>
              </div>

              {/* Tenant info */}
              {c.tenants && (
                <div style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 14,
                  flexWrap: 'wrap'
                }}>
                  <Badge variant="info">
                    <Building2 size={10} />
                    {c.tenants.subscription_tier?.toUpperCase()}
                  </Badge>
                  {c.insightsCount > 0 && (
                    <Badge variant={c.insightsCount > 3 ? 'warning' : 'info'}>
                      <Lightbulb size={10} />
                      {c.insightsCount}
                    </Badge>
                  )}
                  {c.gapsCount > 0 && (
                    <Badge variant="danger">
                      <AlertTriangle size={10} />
                      {c.gapsCount}
                    </Badge>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => handleImpersonate(c)}
              >
                <Crown size={14} /> {t.enterDashboard}
              </Button>
            </Card>
          ))}
        </Grid>
      )}
    </div>
  );
}
